import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { BookOpen, Calendar, ChevronLeft, ChevronRight, Filter, Loader2, PenLine, Download, FileText } from 'lucide-react';
import { logsAPI } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const JournalPage = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [selectedHabit, setSelectedHabit] = useState(null);
  const [exporting, setExporting] = useState(false);
  const LIMIT = 20;

  useEffect(() => {
    const fetchJournal = async () => {
      try {
        setLoading(true);
        const data = await logsAPI.getJournal(LIMIT, 0);
        setEntries(data.entries);
        setTotal(data.total);
        setHasMore(data.hasMore);
        setOffset(0);
      } catch (error) {
        console.error('Error loading journal:', error);
        toast.error('Failed to load journal entries');
      } finally {
        setLoading(false);
      }
    };
    fetchJournal();
  }, []);

  const loadMoreEntries = async () => {
    try {
      setLoadingMore(true);
      const newOffset = offset + LIMIT;
      const data = await logsAPI.getJournal(LIMIT, newOffset);
      setEntries(prev => [...prev, ...data.entries]);
      setTotal(data.total);
      setHasMore(data.hasMore);
      setOffset(newOffset);
    } catch (error) {
      console.error('Error loading more entries:', error);
      toast.error('Failed to load more entries');
    } finally {
      setLoadingMore(false);
    }
  };

  // Export journal to PDF
  const exportToPDF = async () => {
    try {
      setExporting(true);
      
      // Fetch all entries for export (not just the paginated ones)
      let allEntries = entries;
      if (hasMore) {
        const allData = await logsAPI.getJournal(1000, 0);
        allEntries = allData.entries;
      }
      
      // Filter if a habit is selected
      const entriesToExport = selectedHabit 
        ? allEntries.filter(e => e.habitId === selectedHabit)
        : allEntries;
      
      if (entriesToExport.length === 0) {
        toast.error('No journal entries to export');
        return;
      }
      
      // Create PDF
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Title
      doc.setFontSize(24);
      doc.setTextColor(59, 130, 246); // Blue
      doc.text('My Habit Journal', pageWidth / 2, 20, { align: 'center' });
      
      // Subtitle
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      const subtitle = selectedHabit 
        ? `Filtered by: ${entriesToExport[0]?.habitName || 'Unknown Habit'}`
        : `${entriesToExport.length} entries`;
      doc.text(subtitle, pageWidth / 2, 28, { align: 'center' });
      
      // Export date
      doc.setFontSize(10);
      doc.text(`Exported on ${new Date().toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })}`, pageWidth / 2, 35, { align: 'center' });
      
      // Group entries by date for the PDF
      const groupedForPDF = entriesToExport.reduce((groups, entry) => {
        const date = entry.date;
        if (!groups[date]) groups[date] = [];
        groups[date].push(entry);
        return groups;
      }, {});
      
      // Sort dates in descending order
      const sortedDates = Object.keys(groupedForPDF).sort((a, b) => new Date(b) - new Date(a));
      
      let yPosition = 45;
      const lineHeight = 7;
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      
      sortedDates.forEach((dateStr) => {
        const dateEntries = groupedForPDF[dateStr];
        const formattedDate = new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        
        // Check if we need a new page
        if (yPosition > pageHeight - 40) {
          doc.addPage();
          yPosition = 20;
        }
        
        // Date header
        doc.setFontSize(14);
        doc.setTextColor(59, 130, 246);
        doc.setFont(undefined, 'bold');
        doc.text(formattedDate, margin, yPosition);
        yPosition += lineHeight + 2;
        
        // Draw a line under the date
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, yPosition - 3, pageWidth - margin, yPosition - 3);
        
        dateEntries.forEach((entry) => {
          // Check if we need a new page
          if (yPosition > pageHeight - 30) {
            doc.addPage();
            yPosition = 20;
          }
          
          // Habit name and category
          doc.setFontSize(11);
          doc.setTextColor(60, 60, 60);
          doc.setFont(undefined, 'bold');
          const habitText = entry.habitCategory 
            ? `${entry.habitName} (${entry.habitCategory})`
            : entry.habitName;
          doc.text(habitText, margin + 5, yPosition);
          
          // Time
          if (entry.completedAt) {
            const time = new Date(entry.completedAt).toLocaleTimeString('en-US', { 
              hour: 'numeric', 
              minute: '2-digit' 
            });
            doc.setFontSize(9);
            doc.setTextColor(150, 150, 150);
            doc.setFont(undefined, 'normal');
            doc.text(time, pageWidth - margin - 20, yPosition);
          }
          
          yPosition += lineHeight;
          
          // Note content - wrap text
          doc.setFontSize(10);
          doc.setTextColor(80, 80, 80);
          doc.setFont(undefined, 'normal');
          
          const maxWidth = pageWidth - (margin * 2) - 10;
          const splitNotes = doc.splitTextToSize(entry.notes || '', maxWidth);
          
          splitNotes.forEach((line) => {
            if (yPosition > pageHeight - 20) {
              doc.addPage();
              yPosition = 20;
            }
            doc.text(line, margin + 5, yPosition);
            yPosition += lineHeight - 1;
          });
          
          yPosition += 5; // Space between entries
        });
        
        yPosition += 5; // Space between dates
      });
      
      // Footer on last page
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text('Generated by Habit2Goals', pageWidth / 2, pageHeight - 10, { align: 'center' });
      
      // Download the PDF
      const fileName = selectedHabit 
        ? `habit_journal_${entriesToExport[0]?.habitName?.toLowerCase().replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`
        : `habit_journal_${new Date().toISOString().split('T')[0]}.pdf`;
      
      doc.save(fileName);
      toast.success('Journal exported successfully!');
      
    } catch (error) {
      console.error('Error exporting journal:', error);
      toast.error('Failed to export journal');
    } finally {
      setExporting(false);
    }
  };

  // Group entries by date
  const groupedEntries = entries.reduce((groups, entry) => {
    const date = entry.date;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(entry);
    return groups;
  }, {});

  // Get unique habits for filter
  const uniqueHabits = [...new Map(entries.map(e => [e.habitId, { id: e.habitId, name: e.habitName, color: e.habitColor }])).values()];

  // Filter entries by selected habit
  const filteredGroups = selectedHabit 
    ? Object.fromEntries(
        Object.entries(groupedEntries).map(([date, dateEntries]) => [
          date,
          dateEntries.filter(e => e.habitId === selectedHabit)
        ]).filter(([_, dateEntries]) => dateEntries.length > 0)
      )
    : groupedEntries;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const isToday = date.toDateString() === today.toDateString();
    const isYesterday = date.toDateString() === yesterday.toDateString();
    
    if (isToday) return 'Today';
    if (isYesterday) return 'Yesterday';
    
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  };

  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          <p className="text-slate-600 dark:text-slate-400">Loading your journal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/')}
              className="p-2 rounded-xl hover:bg-white/50 dark:hover:bg-slate-800/50 transition-colors"
              data-testid="back-to-dashboard"
            >
              <ChevronLeft className="w-6 h-6 text-slate-600 dark:text-slate-400" />
            </button>
            <div>
              <h1 className="text-3xl font-bold gradient-text flex items-center gap-3">
                <BookOpen className="w-8 h-8" />
                My Journal
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mt-1">
                {total} {total === 1 ? 'entry' : 'entries'} in your habit journal
              </p>
            </div>
          </div>
          
          {/* Export PDF Button */}
          {entries.length > 0 && (
            <Button
              onClick={exportToPDF}
              disabled={exporting}
              data-testid="export-pdf-btn"
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg"
            >
              {exporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Export PDF
                </>
              )}
            </Button>
          )}
        </div>

        {/* Filter by Habit */}
        {uniqueHabits.length > 1 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Filter by habit:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedHabit(null)}
                data-testid="filter-all"
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                  !selectedHabit
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md'
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
                }`}
              >
                All Habits
              </button>
              {uniqueHabits.map((habit) => (
                <button
                  key={habit.id}
                  onClick={() => setSelectedHabit(selectedHabit === habit.id ? null : habit.id)}
                  data-testid={`filter-${habit.id}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
                    selectedHabit === habit.id
                      ? 'text-white shadow-md'
                      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
                  }`}
                  style={selectedHabit === habit.id ? { background: `linear-gradient(135deg, ${habit.color}, ${habit.color}dd)` } : {}}
                >
                  {habit.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {entries.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center">
              <PenLine className="w-10 h-10 text-blue-500" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-800 dark:text-slate-100 mb-2">
              No journal entries yet
            </h2>
            <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto mb-6">
              Start adding notes when you complete your habits. Your reflections and thoughts will appear here as a timeline.
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white"
            >
              Go to Dashboard
            </Button>
          </div>
        )}

        {/* Timeline */}
        {Object.keys(filteredGroups).length > 0 && (
          <div className="space-y-8">
            {Object.entries(filteredGroups).map(([date, dateEntries]) => (
              <div key={date} className="relative">
                {/* Date Header */}
                <div className="sticky top-0 z-10 bg-gradient-to-r from-slate-50/95 via-blue-50/95 to-purple-50/95 dark:from-slate-950/95 dark:via-slate-900/95 dark:to-slate-950/95 backdrop-blur-sm py-3 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                      {formatDate(date)}
                    </h2>
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      {dateEntries.length} {dateEntries.length === 1 ? 'note' : 'notes'}
                    </span>
                  </div>
                </div>

                {/* Entries for this date */}
                <div className="space-y-4 ml-5 pl-8 border-l-2 border-slate-200 dark:border-slate-700">
                  {dateEntries.map((entry) => {
                    const IconComponent = Icons[entry.habitIcon] || Icons.Circle;
                    return (
                      <div 
                        key={entry.id}
                        data-testid={`journal-entry-${entry.id}`}
                        className="relative gradient-card rounded-xl p-5 border border-white/20 dark:border-white/10 hover:shadow-lg transition-all duration-200"
                      >
                        {/* Timeline dot */}
                        <div 
                          className="absolute -left-[41px] top-6 w-4 h-4 rounded-full border-2 border-white dark:border-slate-900 shadow-md"
                          style={{ backgroundColor: entry.habitColor }}
                        />

                        {/* Entry header */}
                        <div className="flex items-center gap-3 mb-3">
                          <div 
                            className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm"
                            style={{ background: `linear-gradient(135deg, ${entry.habitColor}30, ${entry.habitColor}50)` }}
                          >
                            <IconComponent className="w-5 h-5" style={{ color: entry.habitColor }} />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-slate-800 dark:text-slate-100">
                              {entry.habitName}
                            </h3>
                            {entry.habitCategory && (
                              <span className="text-xs text-slate-500 dark:text-slate-400">
                                {entry.habitCategory}
                              </span>
                            )}
                          </div>
                          <span className="text-sm text-slate-400 dark:text-slate-500">
                            {formatTime(entry.completedAt)}
                          </span>
                        </div>

                        {/* Note content */}
                        <div className="ml-13 pl-13">
                          <p className="text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                            {entry.notes}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Load More Button */}
        {hasMore && (
          <div className="mt-8 text-center">
            <Button
              onClick={() => loadMoreEntries()}
              disabled={loadingMore}
              variant="outline"
              className="px-8"
              data-testid="load-more-btn"
            >
              {loadingMore ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  Load More Entries
                  <ChevronRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default JournalPage;
